import React, { useState, useEffect } from "react";
import { database } from "../../firebaseConfig";
import { ref, onValue, remove } from "firebase/database";
import "./OfferDashboard/OfferDashboard.css";

const OfferDashboard = ({ userId, onCreateOffer, onEditOffer }) => {
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [expandedOfferId, setExpandedOfferId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  // Load offers from the database
  useEffect(() => {
    if (!userId) return;

    const offersRef = ref(database, `/World/${userId}/offers`);
    
    const unsubscribe = onValue(offersRef, (snapshot) => {
      if (snapshot.exists()) {
        const offersData = snapshot.val();
        const offersArray = Object.entries(offersData).map(([id, data]) => ({
          id,
          ...data
        }));
        setOffers(offersArray);
      } else {
        setOffers([]);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Delete offer
  const handleDeleteOffer = (offerId) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;

    const offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
    remove(offerRef)
      .then(() => {
        showNotification("Offer successfully deleted", "success");
      })
      .catch((error) => {
        console.error("Error deleting offer:", error);
        showNotification("Error deleting offer", "error");
      });
  };

  // Edit offer
  const handleEditOffer = (offerId) => {
    // Find offer
    const offerToEdit = offers.find(offer => offer.id === offerId);
    
    if (offerToEdit) {
      // Call callback with the offer to edit
      onEditOffer(offerToEdit);
    } else {
      showNotification("Offer could not be found", "error");
    }
  };

  // Duplicate offer
  const handleDuplicateOffer = (offer) => {
    // Show message that function is still being implemented
    showNotification("Duplicate function is still being implemented", "info");
  };

  // Expand/collapse offer details
  const toggleExpandOffer = (offerId) => {
    setExpandedOfferId(expandedOfferId === offerId ? null : offerId);
  };

  // Show and hide notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    
    // Automatically hide after 3 seconds
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  // Sort offers
  const handleSort = (field) => {
    if (sortBy === field) {
      // If already sorted by this field, reverse direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Otherwise sort by new field (default descending)
      setSortBy(field);
      setSortDirection("desc");
    }
  };

  // Filter and sort offers
  const filteredAndSortedOffers = [...offers]
    .filter(offer => 
      offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0;
      
      // Sort by selected field
      if (sortBy === "title") {
        comparison = a.title.localeCompare(b.title);
      } else if (sortBy === "createdAt") {
        comparison = a.createdAt - b.createdAt;
      } else if (sortBy === "discountedTotal") {
        comparison = a.discountedTotal - b.discountedTotal;
      }
      
      // Consider sort direction
      return sortDirection === "asc" ? comparison : -comparison;
    });

  // Display formatted date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR"
    }).format(price);
  };

  // Generate link to offer
  const generateOfferLink = (offerId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/custom-offer/${userId}+${offerId}`;
  };

  // Copy link to clipboard
  const copyOfferLink = (offerId) => {
    const link = generateOfferLink(offerId);
    navigator.clipboard.writeText(link)
      .then(() => {
        setCopiedLinkId(offerId);
        showNotification("Link copied to clipboard", "success");
        
        // Reset after 3 seconds
        setTimeout(() => {
          setCopiedLinkId(null);
        }, 3000);
      })
      .catch((error) => {
        console.error("Error copying link:", error);
        showNotification("Error copying link", "error");
      });
  };

  // Open link in new tab
  const openOfferLink = (offerId) => {
    const link = generateOfferLink(offerId);
    window.open(link, '_blank');
  };

  return (
    <div className="offer-dashboard">
      {notification.message && (
        <div className={`offer-dashboard__notification offer-dashboard__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="offer-dashboard__header">
        <h2 className="offer-dashboard__title">My Offers</h2>
        <div className="offer-dashboard__search">
          <input
            type="text"
            placeholder="Search offers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="offer-dashboard__search-input"
          />
          {searchTerm && (
            <button 
              className="offer-dashboard__clear-search" 
              onClick={() => setSearchTerm("")}
            >
              ×
            </button>
          )}
        </div>
      </div>
      
      {isLoading ? (
        <div className="offer-dashboard__loading">
          <div className="offer-dashboard__spinner"></div>
          <p>Loading offers...</p>
        </div>
      ) : offers.length === 0 ? (
        <div className="offer-dashboard__empty-state">
          <div className="offer-dashboard__empty-icon">
            <img 
              src="/img/bag.svg" 
              alt="No offers yet" 
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                e.target.parentNode.innerHTML = "📝";
              }}
              style={{ maxWidth: "40px", maxHeight: "40px" }}
            />
          </div>
          <h3 className="offer-dashboard__empty-title">No offers yet</h3>
          <p className="offer-dashboard__empty-description">
            Create your first offer to see it here. Offers help you impress potential customers with your services.
          </p>
          <button 
            className="offer-dashboard__create-btn"
            onClick={onCreateOffer}
          >
            Create First Offer
            <span className="offer-dashboard__btn-icon">✨</span>
          </button>
        </div>
      ) : (
        <div className="offer-dashboard__content">
          <div className="offer-dashboard__actions-bar">
            <button 
              className="offer-dashboard__new-offer-btn"
              onClick={onCreateOffer}
            >
              <span className="offer-dashboard__new-offer-icon">+</span>
              Create New Offer
            </button>
          </div>
          
          <div className="offer-dashboard__table-controls">
            <div className="offer-dashboard__entries-count">
              {filteredAndSortedOffers.length} {filteredAndSortedOffers.length === 1 ? "offer" : "offers"} found
            </div>
            <div className="offer-dashboard__sort-controls">
              <span>Sort by:</span>
              <button 
                className={`offer-dashboard__sort-btn ${sortBy === "createdAt" ? "offer-dashboard__sort-btn--active" : ""}`}
                onClick={() => handleSort("createdAt")}
              >
                Date {sortBy === "createdAt" && (sortDirection === "asc" ? "↑" : "↓")}
              </button>
              <button 
                className={`offer-dashboard__sort-btn ${sortBy === "title" ? "offer-dashboard__sort-btn--active" : ""}`}
                onClick={() => handleSort("title")}
              >
                Title {sortBy === "title" && (sortDirection === "asc" ? "↑" : "↓")}
              </button>
              <button 
                className={`offer-dashboard__sort-btn ${sortBy === "discountedTotal" ? "offer-dashboard__sort-btn--active" : ""}`}
                onClick={() => handleSort("discountedTotal")}
              >
                Price {sortBy === "discountedTotal" && (sortDirection === "asc" ? "↑" : "↓")}
              </button>
            </div>
          </div>

          <div className="offer-dashboard__table">
            <div className="offer-dashboard__table-header">
              <div className="offer-dashboard__header-cell offer-dashboard__cell--date">Date</div>
              <div className="offer-dashboard__header-cell offer-dashboard__cell--title">Offer Title</div>
              <div className="offer-dashboard__header-cell offer-dashboard__cell--price">Price</div>
              <div className="offer-dashboard__header-cell offer-dashboard__cell--actions">Actions</div>
            </div>
            
            <div className="offer-dashboard__table-body">
              {filteredAndSortedOffers.map(offer => (
                <React.Fragment key={offer.id}>
                  <div 
                    className={`offer-dashboard__table-row ${expandedOfferId === offer.id ? "offer-dashboard__table-row--expanded" : ""}`}
                    onClick={() => toggleExpandOffer(offer.id)}
                  >
                    <div className="offer-dashboard__cell offer-dashboard__cell--date">
                      {formatDate(offer.createdAt)}
                    </div>
                    <div className="offer-dashboard__cell offer-dashboard__cell--title">
                      {offer.logo && (
                        <img 
                          src={offer.logo} 
                          alt="Logo" 
                          className="offer-dashboard__offer-logo" 
                        />
                      )}
                      <span className="offer-dashboard__offer-title">{offer.title}</span>
                    </div>
                    <div className="offer-dashboard__cell offer-dashboard__cell--price">
                      {formatPrice(offer.discountedTotal)}
                    </div>
                    <div className="offer-dashboard__cell offer-dashboard__cell--actions">
                      <button 
                        className="offer-dashboard__action-btn offer-dashboard__action-btn--preview"
                        onClick={(e) => {
                          e.stopPropagation();
                          openOfferLink(offer.id);
                        }}
                        title="Open offer"
                      >
                        <img src="/img/send.svg" alt="Preview" className="action-icon" />
                      </button>
                      <button 
                        className="offer-dashboard__action-btn offer-dashboard__action-btn--edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditOffer(offer.id);
                        }}
                        title="Edit"
                      >
                        <img src="/img/edit_1.svg" alt="Edit" className="action-icon" />
                      </button>
                      <button 
                        className="offer-dashboard__action-btn offer-dashboard__action-btn--delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOffer(offer.id);
                        }}
                        title="Delete"
                      >
                        <img src="/img/bin.svg" alt="Delete" className="action-icon" />
                      </button>
                    </div>
                  </div>
                  
                  {expandedOfferId === offer.id && (
                    <div className="offer-dashboard__expanded-content">
                      <div 
                        className="offer-dashboard__offer-details"
                        onClick={() => handleEditOffer(offer.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="offer-dashboard__details-header">
                          <h4 className="offer-dashboard__details-title">Click to edit offer</h4>
                          <div className="offer-dashboard__details-subtitle">
                            <img src="/img/edit_1.svg" alt="Edit" className="action-icon" style={{ marginRight: '8px' }} />
                            Click anywhere in this area to edit the offer
                          </div>
                        </div>
                        
                        <div className="offer-dashboard__quick-actions">
                          <button 
                            className="offer-dashboard__btn offer-dashboard__btn--copy"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyOfferLink(offer.id);
                            }}
                          >
                            <span className="offer-dashboard__btn-icon">
                              {copiedLinkId === offer.id ? <img src="/img/check_1.svg" alt="Copied" className="action-icon" /> : <img src="/img/link_1.svg" alt="Copy" className="action-icon" />}
                            </span>
                            {copiedLinkId === offer.id ? "Copied!" : "Copy Link"}
                          </button>
                          <button 
                            className="offer-dashboard__btn offer-dashboard__btn--open"
                            onClick={(e) => {
                              e.stopPropagation();
                              openOfferLink(offer.id);
                            }}
                          >
                            <span className="offer-dashboard__btn-icon">
                              <img src="/img/binoculars.svg" alt="Open" className="action-icon" />
                            </span>
                            Open Offer
                          </button>
                          <button 
                            className="offer-dashboard__btn offer-dashboard__btn--duplicate"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateOffer(offer);
                            }}
                          >
                            <span className="offer-dashboard__btn-icon">
                              <img src="/img/plus_1.svg" alt="Duplicate" className="action-icon" />
                            </span>
                            Duplicate
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferDashboard; 